import { Component, OnInit, OnDestroy, HostBinding } from '@angular/core';
import { LangChangeEvent } from '@ngx-translate/core';
import { SideNavService } from './services/side-nav.service';
import { map, takeUntil, filter } from 'rxjs/operators';
import { SideNav } from './interfaces/side-nav';
import { Router, NavigationEnd } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { LogOutModalComponent } from '@app/components/login/log-out-modal/log-out-modal.component';
import { PersistenceService } from '@app/_services/persistence.service';
import { BaseComponent } from '@app/base/basecomponent';
import { animate, style, transition, trigger } from '@angular/animations';
import { MoveService } from '@app/areas/move-notification/services/move.service';
import { Observable } from 'rxjs';
import { Select } from '@ngxs/store';
import { ListModelState } from '@app/_store/list-model/list-model.state';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate('200ms cubic-bezier(0, 0.2, 0.17, 1)',
        style({ opacity: 1, transform: 'scale(1)' }))]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate('200ms cubic-bezier(0, 0.2, 0.17, 1)',
        style({ opacity: 0, transform: 'scale(0)' }))])
    ]),
  ]
})
export class SideNavComponent extends BaseComponent implements OnInit, OnDestroy {

  @HostBinding('class.navigation-collapsed') navigationCollapsed: boolean = false;
  public bsModalRef: BsModalRef;
  private initialNavigationLoaded: boolean = false;
  public loading: boolean;
  public currentModule: string;
  public dropdownTopPosition: number;
  public homepageTitle: string;
  public isOrgAdmin: boolean = false;
  @Select(ListModelState.moveNotificationCount) moveNotificationCount: Observable<number>;

  constructor(
    public sideNavService: SideNavService,
    private router: Router,
    private moveNotificationService: MoveService,
    private modalService: BsModalService,
    public persistence: PersistenceService,
  ) {
    super();
  }

  public UI = {
    moveNotificationCount: 0,
  }

  ngOnInit() {
    this.subscription.add(this.translate.get('navigation.home').subscribe(value => this.homepageTitle = value));
    this.subscription.add(this.sideNavService.collapsed.subscribe(value => this.navigationCollapsed = value));
    this.subscription.add(this.sideNavService.isOrgAdmin.subscribe(value => this.isOrgAdmin = value));

    const collapsed = localStorage.getItem('avux-ui-full-nav') || null;
    if (JSON.parse(collapsed) || window.innerWidth < 576) {
      this.sideNavService.setCollapse(true);
    }

    this.getNavigation(this.translate.currentLang);

    // Since onLangChange gets fired on page load, this initialNavigationLoaded-thing is a nasty hack to eliminate multiple gets
    this.subscription.add(this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      if (this.initialNavigationLoaded) {
        // reset the navigation before loading new one. Looks better me thinks
        this.sideNavService.navigation = null;
        this.getNavigation(event.lang);
      }
    }));

    this.subscription.add(
      this.router.events.pipe(filter((event => event instanceof NavigationEnd))).subscribe((event: NavigationEnd) => {
        this.checkWindowWidth();
        this.activeModuleClass(event.urlAfterRedirects);
      })
    );
  }

  getNavigation(lang: string) {
    this.loading = true;
    this.cancelHttp.next();
    this.sideNavService.getNavigation(lang).pipe(
      takeUntil(this.cancelHttp),
      map(items => {
        const list = JSON.parse(localStorage.getItem('avux-ui-nav')) || [];
        items.forEach(item => {
          if (!item.page) {
            list.includes(item.ID) ? item.collapse = false : item.collapse = true;
          }
        });
        return items;
      })
    ).subscribe(response => {
      this.loading = false;
      this.sideNavService.navigation = response;

      if (!this.initialNavigationLoaded) {
        this.activeModuleClass(this.router.url);
      }

      this.initialNavigationLoaded = true;
    }, error => {
      this.loading = false;
    });
  }

  onCollapse(item: SideNav, event: MouseEvent) {
    if (!this.navigationCollapsed) {
      this.sideNavService.saveNavItemsCollapseState(item.ID, item.collapse);
      item.collapse = !item.collapse;
      return;
    }

    if (this.navigationCollapsed) {

      let target = event.target as HTMLElement;

      // Fixing issues when user can click the element inside of the button and this causes minor offset for the dropdown position
      if (target.tagName !== 'BUTTON') { target = target.parentElement; }

      // Close other dropdowns when opening a new one
      // Otherwise just toggle the open / closed
      if (item.collapse === true) {
        this.sideNavService.navigation.forEach(nav => {
          if (nav.ID !== item.ID) {
            nav.collapse = true;
          }
        });
      }
      item.collapse = !item.collapse;
      this.dropdownTopPosition = Math.floor(target.getBoundingClientRect().top);
      return;
    }
  }

  onMainNavCollapse() {
    this.sideNavService.setCollapse(!this.navigationCollapsed);

    // add open dropdowns to state when expanding the navigation
    if (this.navigationCollapsed === false) {
      this.sideNavService.navigation.forEach(nav => {
        if (nav.collapse === false) {
          this.sideNavService.saveNavItemsCollapseState(nav.ID, true);
        }
      });
      var list = JSON.parse(localStorage.getItem('avux-ui-nav')) || [];
      return;
    }
    var list = JSON.parse(localStorage.getItem('avux-ui-nav')) || [];
  }

  onOutsideClick() {
    if (this.navigationCollapsed) {
      this.sideNavService.closeAllOpenPanels();
    }
  }

  checkWindowWidth() {
    if (window.innerWidth < 768) {
      this.sideNavService.setCollapse(true);
    }
  }

  openLogOutModal() {
    this.bsModalRef = this.modalService.show(LogOutModalComponent);
  }

  dropdownLinkClick(event: MouseEvent) {
    event.stopPropagation();
    // Add small delay
    setTimeout(() => {
      this.sideNavService.closeAllOpenPanels();
    }, 200);
  }

  activeModuleClass(url: string) {
    if (url === '/') {
      this.currentModule = 'Dashboard';
      return;
    }
    let urlFirst = url.split('/')[1].split('?')[0];

    let module = this.sideNavService.navigation?.find(module => {
      if (module.page) {
        let pageFound = false;
        return "/"+urlFirst === module.page
      }
      else {
        return module.childMenu.find(child =>
          {
            let page = child.page.split('/')[1].split('?')[0];
            return page === urlFirst;
        }
        );
      }
    });

    module ? this.currentModule = module.title : this.currentModule = null;
  }

  expandButtonTitle(): string {
    return this.navigationCollapsed ? this.translate.instant('common.actions.navigation_expand') : this.translate.instant('common.actions.navigation_collapse');
  }

  moduleTitle(title: string): string {
    return this.navigationCollapsed ? title : null;
  }

}
